import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertCircle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  FileText,
  Loader2,
  Pencil,
  RefreshCw,
  RotateCcw,
  Search,
  ShieldCheck,
  User,
  Users,
  Wallet,
  X,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API_BASE = '/api/v1/admin/wallet-users';

const ID_DOCUMENT_TYPES = new Set(['id_card', 'passport', 'drivers_license']);
const POA_DOCUMENT_TYPES = new Set(['utility_bill', 'bank_statement']);

// ID types an admin can SELECT for a user. SA driver's licence and SA temporary
// ID are NOT accepted for KYC (removed 2026-07-30) and are deliberately absent
// from this list — the backend gate (portal updateWalletUserRegistration.js)
// also blocks them. Historical users who already hold one of these legacy
// types are preserved (forward-only); the select below renders a read-only
// "(legacy)" option for them so the existing value stays visible.
const ID_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'south_african_id', label: 'South African ID' },
  { value: 'south_african_passport', label: 'South African Passport' },
  { value: 'international_passport', label: 'International Passport' },
  { value: 'generic_passport', label: 'Passport (other)' },
  { value: 'generic_id', label: 'National ID (other)' },
];
const REMOVED_ID_TYPE_LABELS: Record<string, string> = {
  south_african_driving_license: "South African Driver's Licence (legacy — not accepted)",
  south_african_temporary_id: 'Temporary ID (legacy — not accepted)',
};

const fmtZar = (amount: number | string | null | undefined) => {
  const n = typeof amount === 'string' ? parseFloat(amount) : Number(amount);
  if (amount == null || Number.isNaN(n)) {
    return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(0);
  }
  return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(n);
};

const fmtDate = (d: string | null | undefined) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-ZA');
};

type KycFilter = '' | 'pending' | 'approved' | 'rejected' | 'reset';

interface WalletSummary {
  id?: number;
  balance?: number | string | null;
  currency?: string | null;
  status?: string | null;
}

export interface WalletUserRow {
  id: number;
  phoneNumber?: string | null;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  kycStatus?: string | null;
  kycTier?: number | string | null;
  status?: string | null;
  isActiveWallet?: boolean;
  wallet?: WalletSummary | null;
  createdAt?: string | null;
}

interface ListPagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface RegisteredProfile {
  firstName?: string | null;
  lastName?: string | null;
  fullName?: string | null;
  phoneNumber?: string | null;
  email?: string | null;
  idType?: string | null;
  idTypeLabel?: string | null;
  idNumber?: string | null;
  idVerified?: boolean;
}

interface VerifiedProfileFields {
  fullName?: string;
  surname?: string;
  forenames?: string;
  idNumber?: string;
  dateOfBirth?: string;
  dateIssued?: string;
  validFrom?: string;
  expiryDate?: string;
  licenseNumber?: string;
  nationality?: string;
  documentSubType?: string;
  accountHolder?: string;
  streetAddress?: string;
  suburb?: string;
  city?: string;
  postalCode?: string;
  province?: string;
  documentDate?: string;
  documentType?: string;
}

interface VerifiedProfile {
  documentType?: string | null;
  submittedAt?: string | null;
  hasOcrData?: boolean;
  fields?: VerifiedProfileFields | null;
}

interface ValidationResult {
  isValid?: boolean;
  score?: number;
  errors?: string[];
  warnings?: string[];
  blockingWarnings?: string[];
  informationalWarnings?: string[];
  requiresManualReview?: boolean;
  documentType?: string;
  mode?: string;
  extractedFields?: string[];
  /** Document row status when available — used to avoid false green PASS */
  documentStatus?: string | null;
}

interface KycDetail {
  id?: number;
  status?: string;
  documentType?: string;
  submittedAt?: string;
  reviewedAt?: string;
  reviewedBy?: string;
  rejectionReason?: string;
  verificationScore?: number | string;
  isAutomated?: boolean;
  validationResult?: ValidationResult | null;
}

interface KycDocumentProfile {
  id?: number;
  documentType?: string;
  status?: string;
  submittedAt?: string | null;
  reviewedAt?: string | null;
  reviewedBy?: string | null;
  rejectionReason?: string | null;
  verificationScore?: number | string;
  isAutomated?: boolean;
  validationResult?: ValidationResult | null;
  createdAt?: string;
  updatedAt?: string;
  verifiedProfile?: VerifiedProfile | null;
}

interface WalletUserDetail {
  user?: {
    id?: number;
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
    email?: string;
    kycStatus?: string;
    kycTier?: number | string;
    kycVerifiedAt?: string;
    isActiveWallet?: boolean;
    createdAt?: string;
    pepDeclared?: boolean | null;
    pepDeclaredAt?: string | null;
    pepStaffFlag?: boolean | null;
    pepStaffFlaggedAt?: string | null;
    pepStaffFlaggedBy?: string | null;
  };
  wallet?: {
    balance?: number | string;
    currency?: string;
    status?: string;
    walletId?: string;
  } | null;
  kyc?: KycDetail | null;
  kycDocuments?: KycDocumentProfile[];
  registeredProfile?: RegisteredProfile | null;
  verifiedProfile?: VerifiedProfile | null;
  dataWarning?: string | null;
  recentTransactions?: Array<{
    id?: number;
    amount?: number | string;
    type?: string;
    status?: string;
    description?: string;
    createdAt?: string;
  }>;
}

function authHeaders(): HeadersInit {
  const token = sessionStorage.getItem('portal_token');
  const h: Record<string, string> = { Accept: 'application/json' };
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
}

function formatKycStatus(status: string | null | undefined): string {
  const s = (status || '').toLowerCase();
  const labels: Record<string, string> = {
    verified: 'Verified',
    approved: 'Approved',
    pending: 'Pending',
    rejected: 'Rejected',
    reset: 'Reset',
    not_started: 'Not started',
    ussd_basic: 'USSD Basic',
  };
  return labels[s] || (status ? status.replace(/_/g, ' ') : '—');
}

function kycDot(status: string | null | undefined): string {
  const s = (status || '').toLowerCase();
  switch (s) {
    case 'approved':
    case 'verified':
      return 'bg-emerald-500';
    case 'pending':
      return 'bg-amber-500';
    case 'rejected':
      return 'bg-red-500';
    case 'reset':
      return 'bg-gray-400';
    default:
      return 'bg-gray-400';
  }
}

function kycTextClass(status: string | null | undefined): string {
  const s = (status || '').toLowerCase();
  switch (s) {
    case 'approved':
    case 'verified':
      return 'text-emerald-700';
    case 'pending':
      return 'text-amber-700';
    case 'rejected':
      return 'text-red-700';
    default:
      return 'text-[var(--muted-foreground)]';
  }
}

function isUserActive(u: WalletUserRow): boolean {
  if (typeof u.isActiveWallet === 'boolean') return u.isActiveWallet;
  return (u.status || '').toLowerCase() === 'active';
}

function displayName(u: WalletUserRow): string {
  const a = (u.firstName || '').trim();
  const b = (u.lastName || '').trim();
  const n = `${a} ${b}`.trim();
  return n || '—';
}

function formatDocType(docType: string | null | undefined): string {
  if (!docType) return '—';
  return docType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Heuristic: flag OCR fields mentioned in validation errors/warnings. */
function fieldHasMismatch(
  fieldKey: string,
  validation: ValidationResult | null | undefined,
): boolean {
  if (!validation) return false;
  const msgs = [...(validation.errors || []), ...(validation.warnings || [])].join(' ').toLowerCase();
  const keywords: Record<string, string[]> = {
    idNumber: ['id number', 'id/passport', 'luhn', 'checksum'],
    surname: ['surname', 'last name'],
    fullName: ['name', 'full name'],
    accountHolder: ['account holder', 'name'],
    dateOfBirth: ['date of birth', 'dob'],
    streetAddress: ['address', 'street'],
    postalCode: ['postal'],
  };
  const terms = keywords[fieldKey] || [fieldKey.toLowerCase()];
  return terms.some((t) => msgs.includes(t));
}


const TableSkeleton: React.FC<{ rows?: number }> = ({ rows = 5 }) => (
  <tbody>
    {Array.from({ length: rows }).map((_, i) => (
      <tr key={i} className="border-b border-[var(--border)]">
        <td className="px-4 py-3"><div className="h-4 w-28 rounded bg-[var(--muted)] animate-pulse" /></td>
        <td className="px-4 py-3"><div className="h-4 w-24 rounded bg-[var(--muted)] animate-pulse" /></td>
        <td className="px-4 py-3"><div className="h-4 w-36 rounded bg-[var(--muted)] animate-pulse" /></td>
        <td className="px-4 py-3"><div className="h-4 w-16 rounded bg-[var(--muted)] animate-pulse" /></td>
        <td className="px-4 py-3"><div className="h-4 w-8 rounded bg-[var(--muted)] animate-pulse" /></td>
        <td className="px-4 py-3"><div className="h-4 w-20 rounded bg-[var(--muted)] animate-pulse" /></td>
        <td className="px-4 py-3"><div className="h-3 w-3 rounded-full bg-[var(--muted)] animate-pulse mx-auto" /></td>
        <td className="px-4 py-3"><div className="h-4 w-20 rounded bg-[var(--muted)] animate-pulse" /></td>
      </tr>
    ))}
  </tbody>
);

const DetailRow: React.FC<{ label: string; value: React.ReactNode; mono?: boolean }> = ({
  label,
  value,
  mono,
}) => (
  <div className="flex items-start justify-between gap-4 px-4 py-2.5">
    <dt className="shrink-0 text-xs font-medium uppercase tracking-wider text-[var(--muted-foreground)]">{label}</dt>
    <dd className={`text-right text-sm text-[var(--foreground)] ${mono ? 'font-mono tabular-nums' : ''}`}>{value}</dd>
  </div>
);

const DocumentOcrPanel: React.FC<{
  title: string;
  doc: KycDocumentProfile | null | undefined;
  emptyMessage: string;
  userId?: number;
  onViewDocument?: (userId: number, docId: number) => void;
  reviewUrlLoading?: boolean;
  reviewUrlError?: string | null;
}> = ({ title, doc, emptyMessage, userId, onViewDocument, reviewUrlLoading, reviewUrlError }) => {
  const verified = doc?.verifiedProfile;
  const validation = doc?.validationResult;
  const ocrFields = verified?.fields || {};
  const isPoa =
    doc?.documentType === 'utility_bill' || doc?.documentType === 'bank_statement';

  const ocrIdRows: { key: string; label: string; value: string }[] = [
    { key: 'fullName', label: 'Full name', value: ocrFields.fullName || ocrFields.accountHolder || '—' },
    { key: 'surname', label: 'Surname', value: ocrFields.surname || '—' },
    { key: 'forenames', label: 'Forenames', value: ocrFields.forenames || '—' },
    { key: 'idNumber', label: 'ID / Passport number', value: ocrFields.idNumber || '—' },
    { key: 'dateOfBirth', label: 'Date of birth', value: ocrFields.dateOfBirth ? fmtDate(ocrFields.dateOfBirth) : '—' },
    { key: 'expiryDate', label: 'Expiry date', value: ocrFields.expiryDate ? fmtDate(ocrFields.expiryDate) : '—' },
    { key: 'licenseNumber', label: 'Licence number', value: ocrFields.licenseNumber || '—' },
    { key: 'nationality', label: 'Nationality', value: ocrFields.nationality || '—' },
  ];

  const ocrPoaRows: { key: string; label: string; value: string }[] = [
    { key: 'accountHolder', label: 'Account holder', value: ocrFields.accountHolder || '—' },
    { key: 'surname', label: 'Surname', value: ocrFields.surname || '—' },
    { key: 'streetAddress', label: 'Street address', value: ocrFields.streetAddress || '—' },
    { key: 'suburb', label: 'Suburb', value: ocrFields.suburb || '—' },
    { key: 'city', label: 'City', value: ocrFields.city || '—' },
    { key: 'postalCode', label: 'Postal code', value: ocrFields.postalCode || '—' },
    { key: 'province', label: 'Province', value: ocrFields.province || '—' },
    { key: 'documentDate', label: 'Document date', value: ocrFields.documentDate ? fmtDate(ocrFields.documentDate) : '—' },
  ];

  const ocrRows = isPoa ? ocrPoaRows : ocrIdRows;
  const hasOcrFields = verified?.hasOcrData === true && verified.fields != null;

  const renderBody = () => {
    if (!doc) {
      return (
        <div className="px-4 py-6 text-center">
          <p className="text-sm text-[var(--muted-foreground)]">{emptyMessage}</p>
        </div>
      );
    }

    if (!hasOcrFields) {
      return (
        <div className="divide-y divide-[var(--border)]">
          <div className="border-b border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
            Document on file ({formatDocType(doc.documentType)}) — status: {doc.status || '—'}
            {doc.submittedAt ? ` · submitted ${fmtDate(doc.submittedAt)}` : ''}
            . OCR extraction did not capture readable fields.
          </div>
          <DetailRow label="Document type" value={formatDocType(doc.documentType)} />
          <DetailRow label="Status" value={doc.status || '—'} />
          <DetailRow label="Submitted" value={fmtDate(doc.submittedAt)} />
        </div>
      );
    }

    return (
      <dl className="divide-y divide-[var(--border)]">
        {ocrRows.map((row) => {
          const mismatch = fieldHasMismatch(row.key, validation);
          return (
            <div
              key={row.key}
              className={`flex items-start justify-between gap-4 px-4 py-2.5 ${
                mismatch ? 'border-l-4 border-l-amber-400 bg-amber-50/50' : ''
              }`}
            >
              <dt className="shrink-0 text-xs font-medium uppercase tracking-wider text-[var(--muted-foreground)]">
                {row.label}
                {mismatch && (
                  <span className="ml-1 text-amber-600" title="Validation flagged this field">*</span>
                )}
              </dt>
              <dd
                className={`text-right text-sm text-[var(--foreground)] ${
                  row.key === 'idNumber' || row.key === 'licenseNumber' ? 'font-mono tabular-nums' : ''
                }`}
              >
                {row.value}
              </dd>
            </div>
          );
        })}
      </dl>
    );
  };

  return (
    <div className="rounded-xl border border-[var(--border)] overflow-hidden">
      <div className="border-b border-[var(--border)] bg-[var(--muted)]/60 px-4 py-2.5">
        <h5 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[var(--foreground)]">
          <ShieldCheck className="h-3.5 w-3.5 text-[var(--mymoolah-blue,#2D8CCA)]" aria-hidden />
          {title}
        </h5>
        {doc ? (
          <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[10px]">
            <span className="rounded bg-[var(--background)] px-1.5 py-0.5 font-medium text-[var(--foreground)] border border-[var(--border)]">
              {formatDocType(doc.documentType)}
            </span>
            <span className={`rounded px-1.5 py-0.5 font-medium ${
              doc.status === 'approved' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
              doc.status === 'rejected' ? 'bg-rose-50 text-rose-700 border border-rose-200' :
              'bg-amber-50 text-amber-700 border border-amber-200'
            }`}>
              {doc.status || 'pending'}
            </span>
            <span className="text-[var(--muted-foreground)]">
              Uploaded: {fmtDate(doc.submittedAt || doc.createdAt)}
            </span>
            {doc.updatedAt && doc.updatedAt !== doc.createdAt && doc.updatedAt !== doc.submittedAt ? (
              <span className="text-[var(--muted-foreground)]">
                · Updated: {fmtDate(doc.updatedAt)}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
      {renderBody()}
      {doc?.id != null && userId != null && onViewDocument && (
        <div className="border-t border-[var(--border)] px-4 py-3">
          <button
            type="button"
            onClick={() => onViewDocument(userId, doc.id!)}
            disabled={reviewUrlLoading}
            className="inline-flex items-center gap-2 rounded-lg bg-[var(--primary)] px-3 py-1.5 text-xs font-medium text-[var(--primary-foreground)] transition hover:opacity-90 disabled:opacity-60"
          >
            {reviewUrlLoading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            ) : (
              <FileText className="h-3.5 w-3.5" aria-hidden />
            )}
            View source document
          </button>
          {reviewUrlError && <p className="mt-1 text-xs text-rose-600">{reviewUrlError}</p>}
        </div>
      )}
    </div>
  );
};

const IdentityComparison: React.FC<{
  registered: RegisteredProfile | null | undefined;
  kycDocuments: KycDocumentProfile[] | null | undefined;
  userFallback?: WalletUserDetail['user'];
  isAdmin?: boolean;
  onEditRegistered?: () => void;
  userId?: number;
  onViewDocument?: (userId: number, docId: number) => void;
  reviewUrlLoading?: boolean;
  reviewUrlError?: string | null;
}> = ({
  registered,
  kycDocuments,
  userFallback,
  isAdmin,
  onEditRegistered,
  userId,
  onViewDocument,
  reviewUrlLoading,
  reviewUrlError,
}) => {
  const reg = registered || {};
  const docs = kycDocuments || [];

  const val = (primary: string | null | undefined, fallback?: string | null) => {
    const v = (primary || fallback || '').trim();
    return v || '—';
  };

  const registeredRows: { key: string; label: string; value: string; mono?: boolean }[] = [
    { key: 'firstName', label: 'First name', value: val(reg.firstName, userFallback?.firstName) },
    { key: 'lastName', label: 'Surname', value: val(reg.lastName, userFallback?.lastName) },
    { key: 'phone', label: 'Phone number', value: val(reg.phoneNumber, userFallback?.phoneNumber) },
    {
      key: 'idType',
      label: 'ID document type',
      value: reg.idTypeLabel || formatDocType(reg.idType) || '—',
    },
    {
      key: 'idNumber',
      label: 'ID / Passport number',
      value: val(reg.idNumber),
      mono: true,
    },
    { key: 'email', label: 'Email address', value: val(reg.email, userFallback?.email) },
  ];

  const identityDoc =
    docs.find((d) => d.documentType && ID_DOCUMENT_TYPES.has(d.documentType)) || null;
  const poaDoc =
    docs.find((d) => d.documentType && POA_DOCUMENT_TYPES.has(d.documentType)) || null;

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="rounded-xl border border-[var(--border)] overflow-hidden">
        <div className="border-b border-[var(--border)] bg-[var(--muted)]/60 px-4 py-2.5">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h5 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[var(--foreground)]">
                <User className="h-3.5 w-3.5 text-[var(--primary)]" aria-hidden />
                Registration data
              </h5>
              <p className="mt-0.5 text-[10px] text-[var(--muted-foreground)]">As entered by the user at sign-up</p>
            </div>
            {isAdmin && onEditRegistered && (
              <button
                type="button"
                onClick={onEditRegistered}
                className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-[var(--border)] bg-white px-2 py-1 text-[10px] font-medium text-[var(--foreground)] hover:bg-[var(--muted)]"
              >
                <Pencil className="h-3 w-3" aria-hidden />
                Edit
              </button>
            )}
          </div>
        </div>
        <dl className="divide-y divide-[var(--border)]">
          {registeredRows.map((row) => (
            <DetailRow key={row.key} label={row.label} value={row.value} mono={row.mono} />
          ))}
        </dl>
      </div>

      <div className="space-y-4">
        <DocumentOcrPanel
          title="Identity document (OCR)"
          doc={identityDoc}
          emptyMessage="No identity document on file."
          userId={userId}
          onViewDocument={onViewDocument}
          reviewUrlLoading={reviewUrlLoading}
          reviewUrlError={reviewUrlError}
        />
        <DocumentOcrPanel
          title="Proof of address (OCR)"
          doc={poaDoc}
          emptyMessage="No proof-of-address document on file."
          userId={userId}
          onViewDocument={onViewDocument}
          reviewUrlLoading={reviewUrlLoading}
          reviewUrlError={reviewUrlError}
        />
      </div>
    </div>
  );
};

const KycDocumentsList: React.FC<{
  documents: KycDocumentProfile[];
  userId?: number;
  onViewDocument?: (userId: number, docId: number) => void;
  reviewUrlLoading?: boolean;
}> = ({ documents, userId, onViewDocument, reviewUrlLoading }) => {
  if (!documents.length) return null;

  return (
    <section>
      <h4 className="mb-3 flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-[var(--muted-foreground)]">
        <FileText className="h-4 w-4" aria-hidden />
        KYC documents
      </h4>
      <div className="space-y-3">
        {documents.map((doc) => (
          <div key={doc.id ?? doc.documentType} className="rounded-xl border border-[var(--border)] overflow-hidden">
            <dl className="divide-y divide-[var(--border)]">
              <DetailRow label="Document type" value={formatDocType(doc.documentType)} />
              <DetailRow label="Status" value={doc.status || '—'} />
              <DetailRow label="Submitted" value={fmtDate(doc.submittedAt)} />
              <DetailRow label="Reviewed" value={fmtDate(doc.reviewedAt)} />
              {doc.reviewedBy && <DetailRow label="Reviewed by" value={doc.reviewedBy} />}
              {doc.verificationScore != null && (
                <DetailRow label="Verification score" value={String(doc.verificationScore)} mono />
              )}
              {doc.rejectionReason && (
                <DetailRow label="Rejection reason" value={doc.rejectionReason} />
              )}
            </dl>
            {doc.id != null && userId != null && onViewDocument && (
              <div className="border-t border-[var(--border)] px-4 py-3">
                <button
                  type="button"
                  onClick={() => onViewDocument(userId, doc.id!)}
                  disabled={reviewUrlLoading}
                  className="inline-flex items-center gap-2 rounded-lg border border-[var(--border)] bg-white px-3 py-1.5 text-xs font-medium text-[var(--foreground)] hover:bg-[var(--muted)] disabled:opacity-60"
                >
                  {reviewUrlLoading ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                  ) : (
                    <FileText className="h-3.5 w-3.5" aria-hidden />
                  )}
                  View document
                </button>
              </div>
            )}
            {doc.validationResult && (
              <div className="border-t border-[var(--border)] p-3">
                <ValidationPanel
                  validation={doc.validationResult}
                  documentStatus={doc.status}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

const ValidationPanel: React.FC<{
  validation: ValidationResult | null | undefined;
  documentStatus?: string | null;
}> = ({ validation: vr, documentStatus }) => {
  if (!vr) return null;
  const errors = Array.isArray(vr.errors) ? vr.errors : [];
  const warnings = Array.isArray(vr.warnings) ? vr.warnings : [];
  const blockingWarnings = Array.isArray(vr.blockingWarnings) ? vr.blockingWarnings : [];
  const informationalWarnings = Array.isArray(vr.informationalWarnings)
    ? vr.informationalWarnings
    : [];
  const status = (documentStatus || vr.documentStatus || '').toLowerCase();
  const rejectedOrReview =
    status === 'rejected' ||
    status === 'under_review' ||
    vr.requiresManualReview === true ||
    blockingWarnings.length > 0;
  // Never show green PASS when the document was rejected or has blocking warnings —
  // isValid alone is misleading (Luhn-retry false positives scored 100 then rejected).
  const showPass = vr.isValid === true && !rejectedOrReview;
  const outcomeLabel = showPass
    ? 'PASS'
    : rejectedOrReview
      ? status === 'rejected'
        ? 'REJECTED'
        : 'BLOCKED'
      : 'FAIL';
  const outcomeClass = showPass
    ? 'text-emerald-600'
    : 'text-rose-600';

  return (
    <div className="rounded-xl border border-[var(--border)] p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium uppercase tracking-wider text-[var(--muted-foreground)]">Validation result</span>
        <span className={`inline-flex items-center gap-1 text-xs font-semibold ${outcomeClass}`}>
          {showPass ? <CheckCircle2 className="h-3.5 w-3.5" aria-hidden /> : <AlertCircle className="h-3.5 w-3.5" aria-hidden />}
          {outcomeLabel}
          {typeof vr.score === 'number' ? ` · ${vr.score}` : ''}
        </span>
      </div>
      {typeof vr.mode === 'string' && (
        <p className="text-xs text-[var(--muted-foreground)]">
          OCR mode: <span className="font-mono">{vr.mode}</span>
        </p>
      )}
      {vr.isValid === true && rejectedOrReview && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">
          Field checks scored as valid, but a blocking policy prevented auto-approve
          {blockingWarnings.length ? ' (see below)' : ''}.
        </p>
      )}
      {errors.length > 0 && (
        <ul className="space-y-1">
          {errors.map((e, i) => (
            <li key={`err-${i}`} className="flex items-start gap-2 text-xs text-rose-600">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden />
              <span>{e}</span>
            </li>
          ))}
        </ul>
      )}
      {blockingWarnings.length > 0 && (
        <div className="space-y-1">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-rose-700">Blocking</p>
          <ul className="space-y-1">
            {blockingWarnings.map((w, i) => (
              <li key={`blk-${i}`} className="flex items-start gap-2 text-xs text-rose-600">
                <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden />
                <span>{w}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {warnings.length > 0 && (
        <ul className="space-y-1">
          {warnings.map((w, i) => (
            <li key={`warn-${i}`} className="flex items-start gap-2 text-xs text-amber-600">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden />
              <span>{w}</span>
            </li>
          ))}
        </ul>
      )}
      {informationalWarnings.length > 0 && (
        <div className="space-y-1">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--muted-foreground)]">Informational</p>
          <ul className="space-y-1">
            {informationalWarnings.map((w, i) => (
              <li key={`info-${i}`} className="flex items-start gap-2 text-xs text-[var(--muted-foreground)]">
                <span>{w}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {errors.length === 0 && warnings.length === 0 && blockingWarnings.length === 0 && (
        <p className="text-xs text-[var(--muted-foreground)]">
          {showPass
            ? 'No errors or warnings — document passed all checks.'
            : 'No field errors listed — see document status / rejection reason above.'}
        </p>
      )}
    </div>
  );
};

export const UserManagementOverlay: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user: portalUser } = useAuth();
  const isPortalAdmin = portalUser?.role === 'admin';

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [kycStatus, setKycStatus] = useState<KycFilter>('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [disbursementClientId, setDisbursementClientId] = useState(
    () => searchParams.get('disbursementClientId') || '',
  );
  const [partnerOptions, setPartnerOptions] = useState<{ id: number; companyName: string; clientCode: string }[]>([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  const [users, setUsers] = useState<WalletUserRow[]>([]);
  const [pagination, setPagination] = useState<ListPagination | null>(null);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  const [drawerUserId, setDrawerUserId] = useState<number | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detailPayload, setDetailPayload] = useState<WalletUserDetail | null>(null);

  const [reviewUrlLoading, setReviewUrlLoading] = useState(false);
  const [reviewUrlError, setReviewUrlError] = useState<string | null>(null);

  const [resetKycDialogOpen, setResetKycDialogOpen] = useState(false);
  const [resetKycConfirmId, setResetKycConfirmId] = useState('');
  const [resetKycLoading, setResetKycLoading] = useState(false);
  const [resetKycError, setResetKycError] = useState<string | null>(null);
  const [resetKycSuccess, setResetKycSuccess] = useState<string | null>(null);
  const [pepFlagLoading, setPepFlagLoading] = useState(false);
  const [pepFlagError, setPepFlagError] = useState<string | null>(null);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    idType: 'south_african_id',
    idNumber: '',
  });
  const [editPhoneDisplay, setEditPhoneDisplay] = useState('');
  const [editConfirmId, setEditConfirmId] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editSuccess, setEditSuccess] = useState<string | null>(null);

  const listAbortRef = useRef<AbortController | null>(null);
  const detailAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, kycStatus, activeOnly, limit, disbursementClientId]);

  useEffect(() => {
    const highlight = searchParams.get('highlightUserId');
    if (highlight) {
      const id = parseInt(highlight, 10);
      if (!Number.isNaN(id)) setDrawerUserId(id);
    }
  }, [searchParams]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/v1/admin/channel-partners?limit=100', { headers: authHeaders() });
        const json = await res.json();
        if (json.success) {
          setPartnerOptions(
            (json.data.partners || []).map((p: { id: number; companyName: string; clientCode: string }) => ({
              id: p.id,
              companyName: p.companyName,
              clientCode: p.clientCode,
            })),
          );
        }
      } catch {
        /* non-fatal */
      }
    })();
  }, []);

  const handleUnauthorized = useCallback(() => {
    sessionStorage.removeItem('portal_token');
    navigate('/admin/login', { replace: true });
  }, [navigate]);

  const fetchList = useCallback(async () => {
    listAbortRef.current?.abort();
    const ac = new AbortController();
    listAbortRef.current = ac;

    setListLoading(true);
    setListError(null);

    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(limit));
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (kycStatus) params.set('kycStatus', kycStatus);
    if (activeOnly) params.set('isActive', 'true');
    if (disbursementClientId) params.set('disbursementClientId', disbursementClientId);

    try {
      const res = await fetch(`${API_BASE}?${params.toString()}`, {
        method: 'GET',
        headers: authHeaders(),
        signal: ac.signal,
      });

      if (res.status === 401) {
        handleUnauthorized();
        return;
      }

      const json = await res.json().catch(() => ({}));

      if (!res.ok) {
        setListError((json as { error?: string }).error || 'Failed to load users');
        setUsers([]);
        setPagination(null);
        return;
      }

      const data = (json as { data?: { users?: WalletUserRow[]; pagination?: ListPagination } }).data;
      setUsers(Array.isArray(data?.users) ? data!.users! : []);
      setPagination(data?.pagination ?? null);
    } catch (e) {
      if ((e as Error).name === 'AbortError') return;
      setListError('Network error while loading users');
      setUsers([]);
      setPagination(null);
    } finally {
      setListLoading(false);
    }
  }, [page, limit, debouncedSearch, kycStatus, activeOnly, disbursementClientId, handleUnauthorized]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const fetchDetail = useCallback(
    async (id: number) => {
      detailAbortRef.current?.abort();
      const ac = new AbortController();
      detailAbortRef.current = ac;

      setDetailLoading(true);
      setDetailError(null);
      setDetailPayload(null);

      try {
        const res = await fetch(`${API_BASE}/${id}`, {
          method: 'GET',
          headers: authHeaders(),
          signal: ac.signal,
        });

        if (res.status === 401) {
          handleUnauthorized();
          return;
        }

        const json = await res.json().catch(() => ({}));

        if (!res.ok) {
          setDetailError((json as { error?: string }).error || 'Failed to load user details');
          return;
        }

        const data = (json as { data?: WalletUserDetail }).data;
        setDetailPayload(data && typeof data === 'object' ? data : null);
      } catch (e) {
        if ((e as Error).name === 'AbortError') return;
        setDetailError('Network error while loading details');
      } finally {
        setDetailLoading(false);
      }
    },
    [handleUnauthorized],
  );

  useEffect(() => {
    if (drawerUserId == null) {
      detailAbortRef.current?.abort();
      setDetailPayload(null);
      setDetailError(null);
      setDetailLoading(false);
      return;
    }
    fetchDetail(drawerUserId);
  }, [drawerUserId, fetchDetail]);

  const totalPages = useMemo(() => {
    if (pagination?.totalPages != null) return Math.max(1, pagination.totalPages);
    return 1;
  }, [pagination]);

  const closeDrawer = () => {
    setDrawerUserId(null);
    setReviewUrlError(null);
    setResetKycDialogOpen(false);
    setResetKycConfirmId('');
    setResetKycError(null);
    setResetKycSuccess(null);
    setEditDialogOpen(false);
    setEditConfirmId('');
    setEditError(null);
    setEditSuccess(null);
  };

  const openEditDialog = () => {
    const reg = detailPayload?.registeredProfile;
    const u = detailPayload?.user;
    setEditForm({
      firstName: reg?.firstName || u?.firstName || '',
      lastName: reg?.lastName || u?.lastName || '',
      email: reg?.email || u?.email || '',
      idType: reg?.idType || 'south_african_id',
      idNumber: reg?.idNumber || '',
    });
    setEditPhoneDisplay(reg?.phoneNumber || u?.phoneNumber || '');
    setEditConfirmId(drawerUserId != null ? String(drawerUserId) : '');
    setEditError(null);
    setEditDialogOpen(true);
  };

  const handleSaveRegistration = useCallback(async () => {
    if (drawerUserId == null) return;
    if (editConfirmId.trim() !== String(drawerUserId)) {
      setEditError(`Type ${drawerUserId} to confirm this change.`);
      return;
    }

    setEditLoading(true);
    setEditError(null);

    try {
      const res = await fetch(`${API_BASE}/${drawerUserId}/registered-profile`, {
        method: 'PATCH',
        headers: {
          ...authHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(editForm),
      });

      if (res.status === 401) {
        handleUnauthorized();
        return;
      }

      const json = await res.json().catch(() => ({}));

      if (!res.ok) {
        setEditError((json as { error?: string }).error || 'Failed to update registration details');
        return;
      }

      setEditDialogOpen(false);
      setEditConfirmId('');
      setEditSuccess(
        (json as { message?: string }).message ||
          'Registration details updated. KYC verification was reset.',
      );
      await fetchDetail(drawerUserId);
      fetchList();
    } catch {
      setEditError('Network error while saving registration details');
    } finally {
      setEditLoading(false);
    }
  }, [drawerUserId, editConfirmId, editForm, handleUnauthorized, fetchDetail, fetchList]);

  const openResetKycDialog = () => {
    setResetKycError(null);
    setResetKycSuccess(null);
    setResetKycConfirmId(drawerUserId != null ? String(drawerUserId) : '');
    setResetKycDialogOpen(true);
  };

  const handleResetKyc = useCallback(async () => {
    if (drawerUserId == null) return;
    if (resetKycConfirmId.trim() !== String(drawerUserId)) {
      setResetKycError(`Type ${drawerUserId} to confirm this reset.`);
      return;
    }

    setResetKycLoading(true);
    setResetKycError(null);

    try {
      const res = await fetch(`${API_BASE}/${drawerUserId}/reset-kyc`, {
        method: 'POST',
        headers: {
          ...authHeaders(),
          'Content-Type': 'application/json',
        },
      });

      if (res.status === 401) {
        handleUnauthorized();
        return;
      }

      const json = await res.json().catch(() => ({}));

      if (!res.ok) {
        setResetKycError((json as { error?: string }).error || 'Failed to reset KYC status');
        return;
      }

      setResetKycDialogOpen(false);
      setResetKycConfirmId('');
      setResetKycSuccess(
        (json as { message?: string }).message || 'KYC status reset. User can re-upload documents.',
      );
      await fetchDetail(drawerUserId);
      fetchList();
    } catch {
      setResetKycError('Network error while resetting KYC status');
    } finally {
      setResetKycLoading(false);
    }
  }, [drawerUserId, resetKycConfirmId, handleUnauthorized, fetchDetail, fetchList]);

  const handlePepStaffFlag = useCallback(async (flagged: boolean) => {
    if (drawerUserId == null) return;
    setPepFlagLoading(true);
    setPepFlagError(null);
    try {
      const res = await fetch('/api/v1/admin/fic-tfs/pep-flag', {
        method: 'POST',
        headers: {
          ...authHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ subjectType: 'user', subjectId: drawerUserId, flagged }),
      });
      if (res.status === 401) {
        handleUnauthorized();
        return;
      }
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setPepFlagError((json as { error?: string }).error || 'Failed to update PEP staff flag');
        return;
      }
      await fetchDetail(drawerUserId);
    } catch {
      setPepFlagError('Network error while updating PEP staff flag');
    } finally {
      setPepFlagLoading(false);
    }
  }, [drawerUserId, handleUnauthorized, fetchDetail]);

  const handleViewDocument = useCallback(async (userId: number, docId: number) => {
    setReviewUrlLoading(true);
    setReviewUrlError(null);
    try {
      const res = await fetch(`/api/v1/kyc/${userId}/documents/${docId}/review-url`, {
        headers: authHeaders(),
      });
      if (res.status === 401) {
        sessionStorage.removeItem('portal_token');
        navigate('/admin/login', { replace: true });
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || `Failed to fetch review URL (${res.status})`);
      }
      const body = await res.json();
      const url = body?.data?.url;
      if (!url) throw new Error('No signed URL returned');
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setReviewUrlError(err instanceof Error ? err.message : 'Failed to load document');
    } finally {
      setReviewUrlLoading(false);
    }
  }, [navigate]);

  const user = detailPayload?.user;
  const wallet = detailPayload?.wallet;
  const kyc = detailPayload?.kyc;
  const kycDocuments = detailPayload?.kycDocuments;
  const registeredProfile = detailPayload?.registeredProfile;
  const dataWarning = detailPayload?.dataWarning;
  const recentTx = detailPayload?.recentTransactions;

  const showFrom = pagination ? (pagination.page - 1) * pagination.limit + 1 : 0;
  const showTo = pagination ? Math.min(pagination.page * pagination.limit, pagination.total) : 0;
  const showTotal = pagination?.total ?? 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-[var(--foreground)]">User Management</h2>
          <p className="mt-1 text-sm text-[var(--muted-foreground)]">
            Search wallet users, review KYC status, balances, and recent activity.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="relative w-full max-w-sm">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--muted-foreground)]"
            aria-hidden
          />
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search user id, phone, email, or name…"
            className="w-full min-h-[44px] rounded-lg border border-[var(--border)] bg-white pl-10 pr-3 text-sm text-[var(--foreground)] outline-none transition-shadow placeholder:text-[var(--muted-foreground)] focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20"
            aria-label="Search users"
          />
        </div>

        <select
          id="kyc-filter"
          value={kycStatus}
          onChange={(e) => setKycStatus(e.target.value as KycFilter)}
          className="min-h-[44px] min-w-[140px] rounded-lg border border-[var(--border)] bg-white px-3 text-sm text-[var(--foreground)] outline-none focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20"
          aria-label="KYC status filter"
        >
          <option value="">KYC: All</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="reset">Reset</option>
        </select>

        <select
          id="partner-filter"
          value={disbursementClientId}
          onChange={(e) => setDisbursementClientId(e.target.value)}
          className="min-h-[44px] min-w-[180px] rounded-lg border border-[var(--border)] bg-white px-3 text-sm text-[var(--foreground)] outline-none focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20"
          aria-label="Channel partner filter"
        >
          <option value="">Partner: All</option>
          {partnerOptions.map((p) => (
            <option key={p.id} value={String(p.id)}>
              {p.companyName} ({p.clientCode})
            </option>
          ))}
        </select>

        <label className="flex min-h-[44px] cursor-pointer items-center gap-2 rounded-lg border border-[var(--border)] bg-white px-3 text-sm text-[var(--foreground)]">
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={(e) => setActiveOnly(e.target.checked)}
            className="h-4 w-4 rounded border-[var(--border)] accent-[var(--primary)]"
          />
          Active only
        </label>
      </div>

      {pagination && !listLoading && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-md bg-[var(--muted)] px-2.5 py-1 text-xs font-medium text-[var(--muted-foreground)]">
            Total: {Intl.NumberFormat('en-ZA').format(showTotal)}
          </span>
        </div>
      )}

      {listError && (
        <div className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <AlertCircle className="h-5 w-5 shrink-0 text-red-600" aria-hidden />
          <p className="flex-1 text-sm text-red-800">{listError}</p>
          <button
            type="button"
            onClick={fetchList}
            className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 transition-colors hover:bg-red-50"
          >
            <RefreshCw className="h-3.5 w-3.5" aria-hidden />
            Retry
          </button>
        </div>
      )}

      <div className="bg-white rounded-xl border border-[var(--border)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[880px]">
            <thead>
              <tr className="bg-[var(--muted)] border-b border-[var(--border)]">
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-[var(--muted-foreground)]">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-[var(--muted-foreground)]">Phone</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-[var(--muted-foreground)]">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-[var(--muted-foreground)]">KYC</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-[var(--muted-foreground)]">Tier</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-[var(--muted-foreground)]">Balance</th>
                <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-[var(--muted-foreground)]">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-[var(--muted-foreground)]">Joined</th>
              </tr>
            </thead>
            {listLoading && users.length === 0 ? (
              <TableSkeleton />
            ) : users.length === 0 ? (
              <tbody>
                <tr>
                  <td colSpan={8} className="px-6 py-16 text-center">
                    <div className="mx-auto flex max-w-xs flex-col items-center gap-3">
                      <Users className="h-10 w-10 text-[var(--muted-foreground)]/40" strokeWidth={1.25} aria-hidden />
                      <p className="text-base font-medium text-[var(--foreground)]">No users found</p>
                      <p className="text-sm text-[var(--muted-foreground)]">
                        Try adjusting your search or filters.
                      </p>
                    </div>
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody>
                {users.map((u) => {
                  const active = isUserActive(u);
                  return (
                    <tr
                      key={u.id}
                      onClick={() => setDrawerUserId(u.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setDrawerUserId(u.id);
                        }
                      }}
                      tabIndex={0}
                      role="button"
                      className="cursor-pointer border-b border-[var(--border)] transition-colors hover:bg-[var(--muted)]/50 focus:bg-[var(--muted)]/50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[var(--primary)]/30"
                    >
                      <td className="px-4 py-3 text-sm font-medium text-[var(--foreground)]">{displayName(u)}</td>
                      <td className="px-4 py-3 text-sm text-[var(--muted-foreground)]">{u.phoneNumber || '—'}</td>
                      <td className="px-4 py-3 text-sm text-[var(--muted-foreground)]">{u.email || '—'}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center gap-1.5">
                          <span className={`h-2 w-2 rounded-full ${kycDot(u.kycStatus)}`} />
                          <span className={`text-xs font-medium ${kycTextClass(u.kycStatus)}`}>
                            {formatKycStatus(u.kycStatus)}
                          </span>
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-[var(--foreground)]">{u.kycTier ?? '—'}</td>
                      <td className="px-4 py-3 text-sm text-right font-mono tabular-nums text-[var(--foreground)]">
                        {fmtZar(u.wallet?.balance)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center gap-1.5">
                          <span className={`h-2 w-2 rounded-full ${active ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                          <span className={`text-xs ${active ? 'text-emerald-700' : 'text-[var(--muted-foreground)]'}`}>
                            {active ? 'Active' : 'Inactive'}
                          </span>
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-[var(--muted-foreground)]">{fmtDate(u.createdAt ?? undefined)}</td>
                    </tr>
                  );
                })}
              </tbody>
            )}
          </table>
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t border-[var(--border)]">
          <div className="flex flex-wrap items-center gap-3 text-sm text-[var(--muted-foreground)]">
            {pagination ? (
              <span>Showing {showFrom}–{showTo} of {Intl.NumberFormat('en-ZA').format(showTotal)}</span>
            ) : (
              <span>Page {page}</span>
            )}
            <label className="flex items-center gap-2">
              <span>Rows</span>
              <select
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
                className="rounded-lg border border-[var(--border)] bg-white px-2 py-1.5 text-sm outline-none focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </label>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={listLoading || page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="inline-flex items-center gap-1 rounded-lg border border-[var(--border)] bg-white px-3 py-2 text-sm font-medium text-[var(--foreground)] transition-colors hover:bg-[var(--muted)] disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ChevronLeft className="h-4 w-4" aria-hidden />
              Previous
            </button>
            <span className="hidden min-w-[3rem] text-center text-sm font-medium text-[var(--foreground)] sm:inline-block">
              {page}
            </span>
            <button
              type="button"
              disabled={listLoading || (pagination != null && page >= totalPages)}
              onClick={() => setPage((p) => p + 1)}
              className="inline-flex items-center gap-1 rounded-lg border border-[var(--border)] bg-white px-3 py-2 text-sm font-medium text-[var(--foreground)] transition-colors hover:bg-[var(--muted)] disabled:cursor-not-allowed disabled:opacity-50"
            >
              Next
              <ChevronRight className="h-4 w-4" aria-hidden />
            </button>
          </div>
        </div>
      </div>

      {listLoading && users.length > 0 && (
        <div className="flex items-center justify-center gap-2 text-sm text-[var(--muted-foreground)]">
          <Loader2 className="h-4 w-4 animate-spin text-[var(--primary)]" aria-hidden />
          Updating results…
        </div>
      )}

      {drawerUserId != null && (
        <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" aria-labelledby="user-drawer-title">
          <button
            type="button"
            className="absolute inset-0 bg-black/20 backdrop-blur-[1px]"
            aria-label="Close drawer"
            onClick={closeDrawer}
          />
          <div className="relative flex h-full w-full max-w-2xl flex-col border-l border-[var(--border)] bg-white shadow-2xl lg:max-w-3xl">
            <div className="flex items-center justify-between border-b border-[var(--border)] px-5 py-4">
              <div className="flex items-center gap-2">
                <User className="h-5 w-5 text-[var(--primary)]" aria-hidden />
                <div>
                  <h3 id="user-drawer-title" className="text-lg font-semibold text-[var(--foreground)]">
                    User Details
                  </h3>
                  {user && (
                    <p className="text-xs text-[var(--muted-foreground)]">
                      {[user.firstName, user.lastName].filter(Boolean).join(' ') || user.email}
                    </p>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={closeDrawer}
                className="rounded-lg p-2 text-[var(--muted-foreground)] transition-colors hover:bg-[var(--muted)] hover:text-[var(--foreground)]"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-5">
              {detailLoading && (
                <div className="flex flex-col items-center justify-center gap-3 py-16">
                  <Loader2 className="h-8 w-8 animate-spin text-[var(--primary)]" aria-hidden />
                  <p className="text-sm text-[var(--muted-foreground)]">Loading user…</p>
                </div>
              )}

              {!detailLoading && detailError && (
                <div className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
                  <AlertCircle className="h-5 w-5 shrink-0 text-red-600" aria-hidden />
                  <p className="flex-1 text-sm text-red-800">{detailError}</p>
                  <button
                    type="button"
                    onClick={() => fetchDetail(drawerUserId)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 transition-colors hover:bg-red-50"
                  >
                    <RefreshCw className="h-3.5 w-3.5" aria-hidden />
                    Retry
                  </button>
                </div>
              )}

              {!detailLoading && !detailError && detailPayload && (
                <div className="space-y-6">
                  {editSuccess && (
                    <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
                      <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" aria-hidden />
                      <p className="text-sm text-emerald-800">{editSuccess}</p>
                    </div>
                  )}

                  {dataWarning && (
                    <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                      <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" aria-hidden />
                      <p className="text-sm text-amber-800">{dataWarning}</p>
                    </div>
                  )}

                  {resetKycSuccess && (
                    <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
                      <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" aria-hidden />
                      <p className="text-sm text-emerald-800">{resetKycSuccess}</p>
                    </div>
                  )}

                  {/* Account summary */}
                  <section>
                    <h4 className="mb-3 text-xs font-medium uppercase tracking-wider text-[var(--muted-foreground)]">
                      Account summary
                    </h4>
                    <div className="rounded-xl border border-[var(--border)] overflow-hidden">
                      <dl className="divide-y divide-[var(--border)]">
                        <DetailRow label="User ID" value={user?.id != null ? String(user.id) : '—'} mono />
                        <DetailRow
                          label="Wallet balance"
                          value={fmtZar(wallet?.balance)}
                          mono
                        />
                        <DetailRow
                          label="Account status"
                          value={
                            <span className={`inline-flex items-center gap-1.5 ${user?.isActiveWallet ? 'text-emerald-700' : ''}`}>
                              <span className={`h-2 w-2 rounded-full ${user?.isActiveWallet ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                              {user?.isActiveWallet ? 'Active' : 'Inactive'}
                            </span>
                          }
                        />
                        <DetailRow label="KYC status" value={formatKycStatus(user?.kycStatus)} />
                        <DetailRow label="KYC tier" value={user?.kycTier != null ? String(user.kycTier) : '—'} />
                        <DetailRow label="KYC verified" value={fmtDate(user?.kycVerifiedAt)} />
                        <DetailRow
                          label="PEP declaration"
                          value={user?.pepDeclared == null ? 'Not answered' : user.pepDeclared ? 'Yes' : 'No'}
                        />
                        <DetailRow
                          label="PEP staff flag"
                          value={
                            <span className="flex flex-wrap items-center gap-2">
                              <span>{user?.pepStaffFlag ? 'Yes' : 'No'}</span>
                              <button
                                type="button"
                                disabled={pepFlagLoading || user?.id == null}
                                className="rounded-md border border-[var(--border)] px-2 py-0.5 text-xs hover:bg-[var(--muted)] disabled:opacity-40"
                                onClick={() => void handlePepStaffFlag(true)}
                              >
                                Set PEP
                              </button>
                              <button
                                type="button"
                                disabled={pepFlagLoading || user?.id == null}
                                className="rounded-md border border-[var(--border)] px-2 py-0.5 text-xs hover:bg-[var(--muted)] disabled:opacity-40"
                                onClick={() => void handlePepStaffFlag(false)}
                              >
                                Clear PEP
                              </button>
                            </span>
                          }
                        />
                        {pepFlagError && (
                          <DetailRow label="PEP flag error" value={<span className="text-red-700">{pepFlagError}</span>} />
                        )}
                        <DetailRow label="Joined" value={fmtDate(user?.createdAt)} />
                      </dl>
                    </div>
                  </section>

                  {/* Identity comparison */}
                  <section>
                    <h4 className="mb-3 flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-[var(--muted-foreground)]">
                      <ShieldCheck className="h-4 w-4" aria-hidden />
                      Registration &amp; verification data
                    </h4>
                    <IdentityComparison
                      registered={registeredProfile}
                      kycDocuments={kycDocuments}
                      userFallback={user}
                      isAdmin={isPortalAdmin}
                      onEditRegistered={openEditDialog}
                      userId={user?.id}
                      onViewDocument={handleViewDocument}
                      reviewUrlLoading={reviewUrlLoading}
                      reviewUrlError={reviewUrlError}
                    />
                  </section>

                  <KycDocumentsList
                    documents={kycDocuments || []}
                    userId={user?.id}
                    onViewDocument={handleViewDocument}
                    reviewUrlLoading={reviewUrlLoading}
                  />

                  {/* Legacy single-doc validation (primary ID) */}
                  {!kycDocuments?.length && kyc?.validationResult && (
                    <section>
                      <ValidationPanel
                        validation={kyc.validationResult}
                        documentStatus={kyc.status}
                      />
                    </section>
                  )}

                  {/* Legacy KYC metadata when no multi-doc list */}
                  {!kycDocuments?.length && kyc && (
                    <section>
                      <h4 className="mb-3 flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-[var(--muted-foreground)]">
                        <FileText className="h-4 w-4" aria-hidden />
                        KYC record
                      </h4>
                      <div className="space-y-3">
                        <div className="rounded-xl border border-[var(--border)] overflow-hidden">
                          <dl className="divide-y divide-[var(--border)]">
                            <DetailRow label="Status" value={kyc.status || '—'} />
                            <DetailRow label="Document type" value={formatDocType(kyc.documentType)} />
                            <DetailRow label="Submitted" value={fmtDate(kyc.submittedAt)} />
                            <DetailRow label="Reviewed" value={fmtDate(kyc.reviewedAt)} />
                            {kyc.reviewedBy && <DetailRow label="Reviewed by" value={kyc.reviewedBy} />}
                            {kyc.verificationScore != null && (
                              <DetailRow label="Verification score" value={String(kyc.verificationScore)} mono />
                            )}
                            {kyc.rejectionReason && (
                              <DetailRow label="Rejection reason" value={kyc.rejectionReason} />
                            )}
                          </dl>
                        </div>

                        {typeof kyc.id === 'number' && typeof user?.id === 'number' && (
                          <div className="space-y-1">
                            <button
                              type="button"
                              onClick={() => handleViewDocument(user.id!, kyc.id!)}
                              disabled={reviewUrlLoading}
                              className="inline-flex items-center gap-2 rounded-lg bg-[var(--primary)] px-3 py-1.5 text-xs font-medium text-[var(--primary-foreground)] transition hover:opacity-90 disabled:opacity-60"
                            >
                              {reviewUrlLoading ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                              ) : (
                                <FileText className="h-3.5 w-3.5" aria-hidden />
                              )}
                              View source document
                            </button>
                            {reviewUrlError && (
                              <p className="text-xs text-rose-600">{reviewUrlError}</p>
                            )}
                            <p className="text-xs text-[var(--muted-foreground)]">
                              Opens a 15-minute signed URL in a new tab. The view is audited.
                              {kyc.status === 'under_review' && ' Use Manual Verify after inspecting the document to approve.'}
                            </p>
                          </div>
                        )}
                      </div>
                    </section>
                  )}

                  {/* Wallet details */}
                  {wallet && (
                    <section>
                      <h4 className="mb-3 flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-[var(--muted-foreground)]">
                        <Wallet className="h-4 w-4" aria-hidden />
                        Wallet
                      </h4>
                      <div className="rounded-xl border border-[var(--border)] overflow-hidden">
                        <dl className="divide-y divide-[var(--border)]">
                          {wallet.walletId && <DetailRow label="Wallet ID" value={wallet.walletId} mono />}
                          <DetailRow label="Currency" value={wallet.currency || 'ZAR'} />
                          <DetailRow label="Wallet status" value={wallet.status || '—'} />
                        </dl>
                      </div>
                    </section>
                  )}

                  {/* Recent transactions */}
                  <section>
                    <h4 className="mb-3 text-xs font-medium uppercase tracking-wider text-[var(--muted-foreground)]">
                      Recent transactions
                    </h4>
                    {Array.isArray(recentTx) && recentTx.length > 0 ? (
                      <ul className="space-y-2">
                        {recentTx.map((row, idx) => {
                          const id = row.id ?? idx;
                          return (
                            <li
                              key={String(id)}
                              className="rounded-lg border border-[var(--border)] bg-[var(--muted)]/30 px-3 py-2.5"
                            >
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-sm font-medium font-mono tabular-nums text-[var(--foreground)]">
                                  {fmtZar(row.amount)}
                                </span>
                                <span className="text-xs text-[var(--muted-foreground)]">{fmtDate(row.createdAt)}</span>
                              </div>
                              {(row.type || row.status || row.description) && (
                                <p className="mt-1 text-xs text-[var(--muted-foreground)]">
                                  {[row.type, row.status, row.description].filter(Boolean).join(' · ')}
                                </p>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    ) : (
                      <p className="text-sm text-[var(--muted-foreground)]">No recent transactions.</p>
                    )}
                  </section>

                  {/* Support actions */}
                  <section>
                    <h4 className="mb-3 text-xs font-medium uppercase tracking-wider text-[var(--muted-foreground)]">
                      Support actions
                    </h4>
                    <div className="rounded-xl border border-[var(--border)] bg-[var(--muted)]/20 p-4 space-y-3">
                      <p className="text-sm text-[var(--muted-foreground)]">
                        Reset KYC verification so the user can re-upload documents. Registration data (name, phone, email, ID number) is preserved.
                      </p>
                      <button
                        type="button"
                        onClick={openResetKycDialog}
                        disabled={resetKycLoading || user?.id == null}
                        className="inline-flex items-center gap-2 rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm font-medium text-amber-900 transition-colors hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <RotateCcw className="h-4 w-4" aria-hidden />
                        Reset KYC status
                      </button>
                    </div>
                  </section>
                </div>
              )}
            </div>

            {editDialogOpen && drawerUserId != null && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/30 p-4 overflow-y-auto">
                <div
                  className="my-4 w-full max-w-lg rounded-xl border border-[var(--border)] bg-white p-5 shadow-xl"
                  role="dialog"
                  aria-labelledby="edit-reg-title"
                >
                  <h4 id="edit-reg-title" className="text-lg font-semibold text-[var(--foreground)]">
                    Edit registered details
                  </h4>
                  <p className="mt-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    Saving will update registration data and <strong>re-run KYC validation</strong> against any stored OCR on file. Tier and verification status update automatically.
                  </p>
                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    <label className="block sm:col-span-1">
                      <span className="text-xs font-medium uppercase tracking-wider text-[var(--muted-foreground)]">First name</span>
                      <input
                        type="text"
                        value={editForm.firstName}
                        onChange={(e) => setEditForm((f) => ({ ...f, firstName: e.target.value }))}
                        className="mt-1 w-full rounded-lg border border-[var(--border)] px-3 py-2 text-sm outline-none focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20"
                        disabled={editLoading}
                      />
                    </label>
                    <label className="block sm:col-span-1">
                      <span className="text-xs font-medium uppercase tracking-wider text-[var(--muted-foreground)]">Surname</span>
                      <input
                        type="text"
                        value={editForm.lastName}
                        onChange={(e) => setEditForm((f) => ({ ...f, lastName: e.target.value }))}
                        className="mt-1 w-full rounded-lg border border-[var(--border)] px-3 py-2 text-sm outline-none focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20"
                        disabled={editLoading}
                      />
                    </label>
                    <label className="block sm:col-span-2">
                      <span className="text-xs font-medium uppercase tracking-wider text-[var(--muted-foreground)]">
                        Mobile number (MMTP account)
                      </span>
                      <input
                        type="tel"
                        value={editPhoneDisplay}
                        readOnly
                        className="mt-1 w-full cursor-not-allowed rounded-lg border border-[var(--border)] bg-[var(--muted)]/50 px-3 py-2 text-sm text-[var(--muted-foreground)]"
                        aria-describedby="edit-phone-hint"
                      />
                      <p id="edit-phone-hint" className="mt-1 text-xs text-[var(--muted-foreground)]">
                        Cannot be changed here — this is the user&apos;s wallet account number. Only the user can update it in the app.
                      </p>
                    </label>
                    <label className="block sm:col-span-2">
                      <span className="text-xs font-medium uppercase tracking-wider text-[var(--muted-foreground)]">Email address</span>
                      <input
                        type="email"
                        value={editForm.email}
                        onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
                        className="mt-1 w-full rounded-lg border border-[var(--border)] px-3 py-2 text-sm outline-none focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20"
                        disabled={editLoading}
                      />
                    </label>
                    <label className="block sm:col-span-2">
                      <span className="text-xs font-medium uppercase tracking-wider text-[var(--muted-foreground)]">ID document type</span>
                      <select
                        value={editForm.idType}
                        onChange={(e) => setEditForm((f) => ({ ...f, idType: e.target.value }))}
                        className="mt-1 w-full rounded-lg border border-[var(--border)] px-3 py-2 text-sm outline-none focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20"
                        disabled={editLoading}
                      >
                        {editForm.idType && !ID_TYPE_OPTIONS.some((o) => o.value === editForm.idType) && REMOVED_ID_TYPE_LABELS[editForm.idType] ? (
                          <option value={editForm.idType} disabled>{REMOVED_ID_TYPE_LABELS[editForm.idType]}</option>
                        ) : null}
                        {ID_TYPE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </label>
                    <label className="block sm:col-span-2">
                      <span className="text-xs font-medium uppercase tracking-wider text-[var(--muted-foreground)]">ID / Passport number</span>
                      <input
                        type="text"
                        value={editForm.idNumber}
                        onChange={(e) => setEditForm((f) => ({ ...f, idNumber: e.target.value }))}
                        className="mt-1 w-full rounded-lg border border-[var(--border)] px-3 py-2 font-mono text-sm outline-none focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20"
                        disabled={editLoading}
                        autoComplete="off"
                      />
                    </label>
                  </div>
                  <label htmlFor="edit-reg-confirm" className="mt-4 block text-xs font-medium uppercase tracking-wider text-[var(--muted-foreground)]">
                    Type <span className="font-mono text-[var(--foreground)]">{drawerUserId}</span> to confirm
                  </label>
                  <input
                    id="edit-reg-confirm"
                    type="text"
                    inputMode="numeric"
                    value={editConfirmId}
                    onChange={(e) => setEditConfirmId(e.target.value)}
                    className="mt-1.5 w-full rounded-lg border border-[var(--border)] px-3 py-2 text-sm font-mono outline-none focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20"
                    autoComplete="off"
                    disabled={editLoading}
                  />
                  {editError && <p className="mt-2 text-sm text-red-600">{editError}</p>}
                  <div className="mt-5 flex flex-wrap justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setEditDialogOpen(false);
                        setEditConfirmId('');
                        setEditError(null);
                      }}
                      disabled={editLoading}
                      className="rounded-lg border border-[var(--border)] bg-white px-4 py-2 text-sm font-medium text-[var(--foreground)] hover:bg-[var(--muted)] disabled:opacity-60"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveRegistration}
                      disabled={editLoading || editConfirmId.trim() !== String(drawerUserId)}
                      className="inline-flex items-center gap-2 rounded-lg bg-[var(--primary)] px-4 py-2 text-sm font-medium text-[var(--primary-foreground)] hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {editLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                      ) : (
                        <Pencil className="h-4 w-4" aria-hidden />
                      )}
                      Save changes
                    </button>
                  </div>
                </div>
              </div>
            )}

            {resetKycDialogOpen && drawerUserId != null && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/30 p-4">
                <div
                  className="w-full max-w-md rounded-xl border border-[var(--border)] bg-white p-5 shadow-xl"
                  role="alertdialog"
                  aria-labelledby="reset-kyc-title"
                  aria-describedby="reset-kyc-desc"
                >
                  <h4 id="reset-kyc-title" className="text-lg font-semibold text-[var(--foreground)]">
                    Reset KYC status?
                  </h4>
                  <p id="reset-kyc-desc" className="mt-2 text-sm text-[var(--muted-foreground)]">
                    This resets verification for user ID{' '}
                    <span className="font-mono font-medium text-[var(--foreground)]">{drawerUserId}</span>
                    {user && (
                      <>
                        {' '}
                        ({[user.firstName, user.lastName].filter(Boolean).join(' ') || user.email || 'unknown'})
                      </>
                    )}
                    . KYC records are marked <span className="font-medium">reset</span> (not deleted). Wallet limits return to Tier 0 defaults.
                  </p>
                  <label htmlFor="reset-kyc-confirm" className="mt-4 block text-xs font-medium uppercase tracking-wider text-[var(--muted-foreground)]">
                    Type <span className="font-mono text-[var(--foreground)]">{drawerUserId}</span> to confirm
                  </label>
                  <input
                    id="reset-kyc-confirm"
                    type="text"
                    inputMode="numeric"
                    value={resetKycConfirmId}
                    onChange={(e) => setResetKycConfirmId(e.target.value)}
                    className="mt-1.5 w-full rounded-lg border border-[var(--border)] px-3 py-2 text-sm font-mono outline-none focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20"
                    autoComplete="off"
                    disabled={resetKycLoading}
                  />
                  {resetKycError && (
                    <p className="mt-2 text-sm text-red-600">{resetKycError}</p>
                  )}
                  <div className="mt-5 flex flex-wrap justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setResetKycDialogOpen(false);
                        setResetKycConfirmId('');
                        setResetKycError(null);
                      }}
                      disabled={resetKycLoading}
                      className="rounded-lg border border-[var(--border)] bg-white px-4 py-2 text-sm font-medium text-[var(--foreground)] hover:bg-[var(--muted)] disabled:opacity-60"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleResetKyc}
                      disabled={resetKycLoading || resetKycConfirmId.trim() !== String(drawerUserId)}
                      className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {resetKycLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                      ) : (
                        <RotateCcw className="h-4 w-4" aria-hidden />
                      )}
                      Reset KYC
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
